"use client";

import { useState, useEffect, useMemo } from "react";
import styles from "../../Supplier Ratings/css/supplierRatings.module.css";
import Modal from "../../../../components/Modal";
import RatingsTable from "./RatingsTable";
import RatingModal from "./RatingModal";
import { fetchSupplierRatings, createSupplierRating, updateSupplierRating } from "../../../../api/supplierManagement/supplierRatingsApi";
import { fetchSuppliers } from "../../../../api/supplierManagement/supplierApi";

export default function RatingsModalContainer() {
  const [ratingsData, setRatingsData] = useState([]);
  const [ratingSearch, setRatingSearch] = useState("");
  const [ratingSort, setRatingSort] = useState("");
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [ratingEditIndex, setRatingEditIndex] = useState(-1); // -1 means adding new
  const [supplierOptions, setSupplierOptions] = useState([]);
  const [supplierNameToCode, setSupplierNameToCode] = useState({});
  const [ratingSupplier, setRatingSupplier] = useState("");
  const [ratingForm, setRatingForm] = useState({
    quality: 0,
    reliability: 0,
    delivery: 0,
    cost: 0,
    compliance: 0,
    customerService: 0,
  });

  useEffect(() => {
    (async () => {
      const ratings = await fetchSupplierRatings();
      setRatingsData(ratings);
      const suppliers = await fetchSuppliers();
      const names = (suppliers || []).map(s => s.suppliername).filter(Boolean);
      const map = {};
      (suppliers || []).forEach(s => { if (s.suppliername && s.suppliercode) map[s.suppliername] = s.suppliercode; });
      setSupplierOptions(names);
      setSupplierNameToCode(map);
    })();
  }, []);

  const availableSupplierOptions = useMemo(() => {
    const ratedSuppliers = new Set((ratingsData || []).map(r => r.supplier));
    return (supplierOptions || []).filter(name => !ratedSuppliers.has(name));
  }, [ratingsData, supplierOptions]);

  const handleSaveRating = () => {
    if (!ratingSupplier) return;
    const { quality, reliability, delivery, cost, compliance, customerService } = ratingForm;
    const numeric = { quality, reliability, delivery, cost, compliance, customerService };
    const suppliercode = supplierNameToCode[ratingSupplier] || ratingSupplier;
    (async () => {
      if (ratingEditIndex >= 0) {
        const ratingToUpdate = ratingsData[ratingEditIndex];
        await updateSupplierRating(ratingToUpdate.id, numeric);
      } else {
        await createSupplierRating({ suppliercode, ...numeric });
      }
      const refreshed = await fetchSupplierRatings();
      setRatingsData(refreshed);
      setRatingSupplier("");
      setRatingForm({ quality:0, reliability:0, delivery:0, cost:0, compliance:0, customerService:0 });
      setShowRatingModal(false);
      setRatingEditIndex(-1);
    })();
  };

  const handleEditRating = (row, rowIndex) => {
    setRatingEditIndex(rowIndex);
    setRatingSupplier(row.supplier);
    setRatingForm({
      quality: Number(row.quality || 0),
      reliability: Number(row.reliability || 0),
      delivery: Number(row.delivery || 0),
      cost: Number(row.cost || 0),
      compliance: Number(row.compliance || 0),
      customerService: Number(row.customerService || 0),
    });
    setShowRatingModal(true);
  };

  const handleAddRating = () => {
    setRatingEditIndex(-1);
    setRatingSupplier('');
    setRatingForm({ quality:0, reliability:0, delivery:0, cost:0, compliance:0, customerService:0 });
    setShowRatingModal(true);
  };

  return (
    <>
      <RatingsTable
        ratingsData={ratingsData}
        onEdit={handleEditRating}
        onAddRating={handleAddRating}
        ratingSearch={ratingSearch}
        setRatingSearch={setRatingSearch}
        ratingSort={ratingSort}
        setRatingSort={setRatingSort}
      />

      {showRatingModal && (
        <Modal closeStyle="plain" onClose={() => setShowRatingModal(false)}>
          <RatingModal
            showRatingModal={showRatingModal}
            setShowRatingModal={setShowRatingModal}
            ratingSupplier={ratingSupplier}
            setRatingSupplier={setRatingSupplier}
            ratingForm={ratingForm}
            setRatingForm={setRatingForm}
            ratingEditIndex={ratingEditIndex}
            supplierOptions={availableSupplierOptions}
            onSave={handleSaveRating}
          />
        </Modal>
      )}
    </>
  );
}
